import { generatePath } from 'react-router'
import { ButtonLegacy, buttonVariants } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import {
	DownloadCloud02Icon,
	ImageRejectedIcon,
	StorageIcon
} from '@/components/ui/icons'
import type { UploaderFolderView } from '@/features/host-review/types'
import { findFolder } from '@/features/host-review/utils'
import { PhotosetFinal } from '@/features/photoset/components/photoset-final'
import { routes } from '@/routes'
import { Trans, useTranslation } from '@/translations/i18n'

/**
 * Nothing was published: the host removed the whole set.
 *
 * A terminal screen with no folder split, deliberately - the removed photos are
 * still reachable, in full, from the full-rejection team view, which is where
 * an accidental-removal follow-up is actually run. Repeating the gallery here
 * would invite an operator to act on photos this surface offers no action for.
 */
const FullRejectionOutcome = ({
	reviewId,
	removedPackage
}: {
	reviewId?: string
	removedPackage: UploaderFolderView['removedPackage']
}) => {
	const { t } = useTranslation()

	return (
		<PhotosetFinal
			endSlot={
				<div className='flex flex-col items-center gap-3'>
					{/* The removed archive is the only one a full rejection has -
					    nothing is approved, so nothing is published - and it is what
					    keeps the rejected set available later. */}
					{removedPackage ? (
						<a
							className={buttonVariants({
								size: 'md',
								variant: 'outline'
							})}
							href={removedPackage.url}
							rel='noreferrer'
							target='_blank'
						>
							<DownloadCloud02Icon />
							{t('host_review:full_rejection.download')}
						</a>
					) : null}

					{/* The set as the host saw it, where an accidental rejection is
					    followed up. */}
					{reviewId ? (
						<ButtonLinkLegacy
							size='sm'
							to={generatePath(
								routes.HOST_REVIEW_FULL_REJECTION_DETAIL.path,
								{ reviewId }
							)}
							type='button'
							variant='tertiary'
						>
							{t('host_review:full_rejections.detail.view_set')}
						</ButtonLinkLegacy>
					) : null}
					<ButtonLinkLegacy
						size='sm'
						to={routes.WELCOME.path}
						type='button'
						variant='tertiary'
					>
						{t('photoset:host_selection.back_home')}
					</ButtonLinkLegacy>
				</div>
			}
			icon={<ImageRejectedIcon className='text-neutral-600' size={48} />}
			subtitle={
				<span className='flex flex-col gap-4'>
					<span>{t('host_review:full_rejection.subtitle')}</span>
					{/* The removed set is not lost - it is kept and reachable from
					    the team view - so the copy says so rather than leaving the
					    operator to assume the shoot is gone. */}
					<span>{t('host_review:full_rejection.available')}</span>
				</span>
			}
			title={t('host_review:full_rejection.title')}
		/>
	)
}

/**
 * The host kept some photos: the kept archive is the deliverable, and the
 * removed folder is shown for reference so an accidental removal can be caught.
 */
const KeptSelectionOutcome = ({ view }: { view: UploaderFolderView }) => {
	const { t } = useTranslation()

	const kept = findFolder(view.folders, 'kept')
	const removed = findFolder(view.folders, 'removed')
	const keptCount = view.review?.keptCount ?? kept?.photoCount ?? 0
	const removedCount = view.review?.removedCount ?? removed?.photoCount ?? 0

	return (
		<PhotosetFinal
			endSlot={
				<div className='flex flex-col items-center gap-3'>
					{view.approvedPackage ? (
						// A plain anchor, not a router link: the package URL
						// points at the asset host, so routing must not
						// intercept it.
						<a
							className={buttonVariants({ size: 'md' })}
							href={view.approvedPackage.url}
							rel='noreferrer'
							target='_blank'
						>
							<DownloadCloud02Icon />
							{t('host_review:closed.download_selected', {
								count: keptCount
							})}
						</a>
					) : (
						<>
							<ButtonLegacy disabled size='md' type='button'>
								<DownloadCloud02Icon />
								{t('host_review:folders.package.building')}
							</ButtonLegacy>
							<p className='max-w-md text-center text-neutral-600 text-sm/5'>
								{t('host_review:folders.package.building_hint')}
							</p>
						</>
					)}

					{/* The unselected shots, kept on hand so a photo can be swapped
					    into the selection. Absent when the host removed nothing,
					    which is exactly when there is nothing to offer. */}
					{view.removedPackage ? (
						<a
							className={buttonVariants({
								size: 'md',
								variant: 'outline'
							})}
							href={view.removedPackage.url}
							rel='noreferrer'
							target='_blank'
						>
							<StorageIcon />
							{t('host_review:closed.download_rejected', {
								count: removedCount
							})}
						</a>
					) : null}

					{/* The footnote is about swapping in a shot the host did not
					    pick; with nothing removed there is none to swap. */}
					{removedCount > 0 ? (
						<p className='max-w-md text-center text-neutral-900 text-sm/5'>
							{t('host_review:closed.footnote')}
						</p>
					) : null}

					<ButtonLinkLegacy
						size='sm'
						to={routes.WELCOME.path}
						type='button'
						variant='tertiary'
					>
						{t('photoset:host_selection.back_home')}
					</ButtonLinkLegacy>
				</div>
			}
			status='success'
			subtitle={
				<Trans
					components={{
						strong: <strong className='font-bold' />
					}}
					// With nothing removed there is no unselected folder to keep
					// on hand, so the copy must not offer one.
					i18nKey={
						removedCount === 0
							? 'host_review:closed.subtitle_all_kept'
							: 'host_review:closed.subtitle'
					}
					values={{
						selected: keptCount,
						total: keptCount + removedCount
					}}
				/>
			}
			title={t('host_review:closed.title')}
		/>
	)
}

/**
 * The uploader-facing view of a closed review.
 *
 * Only ever rendered for a closed review - a non-reviewed set keeps today's
 * page, and an open review shows the waiting state, since the contract sends no
 * folders at all while one is open.
 */
export const UploaderFolderViewPanel = ({
	view
}: {
	view: UploaderFolderView
}) => {
	// The review record is the authority here, not the folder contents: a full
	// rejection is terminal and publishes nothing, so it gets its own screen.
	if (view.review?.isFullRejection) {
		return (
			<FullRejectionOutcome
				removedPackage={view.removedPackage}
				reviewId={view.review.reviewId}
			/>
		)
	}

	return <KeptSelectionOutcome view={view} />
}

UploaderFolderViewPanel.displayName = 'UploaderFolderViewPanel'
